import type { PayRunApprovalDto, PayRunDetailDto } from './payRunsApiTypes';
import type { PayRunExceptionsPage } from './payRunPopulationApiTypes';

/**
 * PRR-402 lifecycle chrome: the status tag beside the title and the full-width notice above the
 * timeline. Both are pure functions of the data the screen already loads, so they are unit testable
 * and carry no rendering concerns.
 *
 * Where the design shows information the API does not expose — the names of the affected employees
 * and of the pending approvers — the copy falls back to the counts that ARE available rather than
 * inventing names. Those gaps are recorded in the PRR-402 worklog.
 */

export type PayRunStatusTagTone = 'blue' | 'positive' | 'caution' | 'neutral';

export interface PayRunStatusTag {
  label: string;
  tone: PayRunStatusTagTone;
}

export type PayRunNoticeTone = 'caution' | 'positive' | 'info';

export interface PayRunNotice {
  tone: PayRunNoticeTone;
  title: string;
  body: string;
  /** Present when the design offers a route out of the notice. */
  actionLabel?: string;
  /** True when this state is drawn from the design but has no backend behind it. */
  designOnly?: boolean;
}

export interface PayRunNoticeInput {
  payRun: PayRunDetailDto;
  approval: PayRunApprovalDto | null;
  exceptions: PayRunExceptionsPage | null;
}

interface ApprovalProgress {
  submitted: boolean;
  rejected: boolean;
  approved: number;
  required: number;
  allApproved: boolean;
  outstandingApprovers: number;
}

const summariseApproval = (approval: PayRunApprovalDto | null): ApprovalProgress => {
  const live = (approval?.streams ?? []).flatMap((stream) =>
    stream.requirements.filter((requirement) => !requirement.isSuperseded));

  // Group by person: a route carries the same approvers in every stream, so counting requirements
  // would report two approvers as four.
  const byApprover = new Map<string, typeof live>();
  for (const requirement of live) {
    const existing = byApprover.get(requirement.approverUserId);
    if (existing) existing.push(requirement);
    else byApprover.set(requirement.approverUserId, [requirement]);
  }
  const approvers = [...byApprover.values()];

  // An approver counts as approved only once every stream they sit on is approved.
  const approved = approvers.filter((group) =>
    group.every((requirement) => requirement.status === 'approved')).length;
  const rejected = live.some((requirement) => requirement.status === 'rejected');

  return {
    submitted: Boolean(approval?.submittedAt),
    rejected,
    approved,
    required: approvers.length,
    allApproved: approvers.length > 0 && approved === approvers.length,
    outstandingApprovers: approvers.length - approved,
  };
};

const plural = (count: number, singular: string, pluralForm = `${singular}s`): string =>
  `${count} ${count === 1 ? singular : pluralForm}`;

export const buildPayRunStatusTag = ({ payRun, approval }: PayRunNoticeInput): PayRunStatusTag | null => {
  if (payRun.workflowStatus === 'cancelled') return { label: 'Cancelled', tone: 'neutral' };
  if (payRun.workflowStatus === 'complete') return { label: 'Complete', tone: 'positive' };

  const progress = summariseApproval(approval);
  if (progress.rejected) return { label: 'Changes requested', tone: 'caution' };
  if (payRun.workflowStatus === 'eft_ready') return { label: 'Approved', tone: 'positive' };
  if (progress.submitted) return { label: 'Awaiting approval', tone: 'blue' };
  // Only claim readiness when the server actually projects a submittable context: a stale or
  // superseded approval must not be dressed up as ready.
  if (payRun.workflowStatus === 'ready_to_pay' && approval?.canSubmit) {
    return { label: 'Ready to submit', tone: 'blue' };
  }
  return null;
};

export const buildPayRunNotice = ({ payRun, approval, exceptions }: PayRunNoticeInput): PayRunNotice | null => {
  if (payRun.workflowStatus === 'cancelled') {
    return {
      tone: 'caution',
      title: 'Pay run cancelled',
      body: 'This pay run was cancelled and cannot be reopened, edited or recalculated.',
    };
  }

  if (payRun.workflowStatus === 'complete') {
    return {
      tone: 'positive',
      title: 'Pay run complete',
      body: 'Payments have been released and the pay run is closed.',
    };
  }

  const progress = summariseApproval(approval);

  if (progress.rejected) {
    return {
      tone: 'caution',
      title: 'Changes requested',
      body: 'An approver rejected this pay run. The backend cancels a rejected run outright, so the '
        + 'send-back-for-amendment flow in the design is not available yet.',
      designOnly: true,
    };
  }

  if (payRun.workflowStatus === 'eft_ready') {
    return {
      tone: 'positive',
      title: 'Approved',
      body: 'All approvals are in. Release pay to close the run.',
    };
  }

  if (progress.submitted) {
    return {
      tone: 'info',
      title: `${progress.approved} of ${progress.required} approved`,
      body: progress.outstandingApprovers > 0
        ? `Waiting on ${plural(progress.outstandingApprovers, 'approver')}.`
        : 'Waiting on the remaining approvers.',
    };
  }

  // Compliance issues outrank the ready-to-submit message: the operator must clear them first.
  const summary = exceptions?.summary;
  const outstanding = summary ? summary.blocking + summary.warnings + summary.reviewRequired : 0;
  if (outstanding > 0) {
    return {
      tone: 'caution',
      title: `${plural(outstanding, 'issue')} ${outstanding === 1 ? 'needs' : 'need'} your review`,
      body: `${plural(summary?.affectedPeople ?? 0, 'employee')} affected.`,
      actionLabel: `View ${plural(outstanding, 'compliance issue')}`,
    };
  }

  if (payRun.workflowStatus === 'ready_to_pay') {
    return {
      tone: 'positive',
      title: 'Ready to submit for approval',
      body: progress.required > 0
        ? `Send through to ${plural(progress.required, 'approver')}.`
        : 'Send through for approval.',
    };
  }

  return null;
};
